import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useI18n } from "../../i18n/useI18n";
import { type ChartResult } from "../../lib/ephemeris/chart";
import { Button } from "../ui/Button";
import { AppWaitlistModal } from "./AppWaitlistModal";
import { scrollToSectionOnPath } from "../../lib/scroll-to-section";

/** Interactive "Aha Moment" preview modal — free Lagna/Nakshatra + Dasha gate. */
export function ChartModal({ result, onClose }: { result: ChartResult; onClose: () => void }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [showWaitlist, setShowWaitlist] = useState(false);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  // "Premium-Report freischalten": there is no payment/unlock flow yet, so
  // the honest next step is to land the visitor on the offer that actually
  // delivers this (VIP Cosmic Coaching, id "vip-coaching" in
  // supabase-schema.sql's offers seed data) rather than a dead button.
  // Closes this modal first so the destination offer card isn't hidden
  // behind it.
  const goToVipOffer = () => {
    onClose();
    scrollToSectionOnPath("offer-vip-coaching", "/angebote", navigate, location.pathname);
  };

  const openWaitlist = () => setShowWaitlist(true);

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
      {/* data-lenis-prevent: without this, Lenis (see hooks/useLenis.ts —
          it globally intercepts wheel/touch scroll on the whole page for
          its smooth-scroll effect) can swallow touch gestures that start
          inside this fixed overlay, especially on real touchscreens where
          a "tap" is rarely a perfectly stationary press (some finger
          movement is normal and gets read as a scroll/drag attempt).
          This is a documented Lenis integration requirement
          (https://github.com/darkroomengineering/lenis#preventing-scroll)
          for exactly this modal-on-top-of-a-Lenis-page scenario — root
          cause of buttons being unresponsive specifically on mobile. */}
      <div
        data-lenis-prevent
        className="glass relative z-10 w-full max-w-lg animate-[fadeUp_0.4s_ease] p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="eyebrow mb-2 text-center">{t.modal.badge}</p>
        <h3 className="mb-6 text-center text-2xl font-semibold">{t.modal.title}</h3>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-slate-200 bg-white/70 p-4">
            <p className="text-xs text-slate-500">{t.modal.lagna}</p>
            <p className="font-display text-lg text-gold-deep">{result.lagna.name}</p>
            <p className="text-xs text-slate-400">{result.lagna.western} · {result.lagna.element}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white/70 p-4">
            <p className="text-xs text-slate-500">{t.modal.rashi}</p>
            <p className="font-display text-lg text-gold-deep">{result.rashi.name}</p>
            <p className="text-xs text-slate-400">{result.rashi.western}</p>
          </div>
        </div>
        <div className="mt-3 rounded-xl border border-slate-200 bg-white/70 p-4">
          <p className="text-xs text-slate-500">{t.modal.nakshatra}</p>
          <p className="font-display text-lg text-gold">{result.nakshatra}</p>
        </div>

        <div className="mt-4 rounded-xl border border-gold/30 bg-gold/10 p-4">
          <p className="text-sm font-medium">🔒 {t.modal.lockedTitle} — {result.premium.currentDasha}</p>
          <p className="mt-1 text-xs text-slate2/70">{t.modal.lockedBody}</p>
        </div>

        <div className="relative z-20 mt-5 flex flex-col gap-3">
          <Button type="button" className="w-full" onClick={goToVipOffer}>
            {t.modal.unlock}
          </Button>
          <Button type="button" variant="ghost" className="w-full" onClick={openWaitlist}>
            {t.modal.app}
          </Button>
          <button type="button" onClick={onClose} className="text-xs text-slate-400 underline-offset-2 hover:underline">
            {t.modal.again}
          </button>
        </div>
      </div>
    </div>
    {showWaitlist && <AppWaitlistModal onClose={() => setShowWaitlist(false)} />}
    </>
  );
}
